/**
 * AI CONTEXT
 * Purpose: builds makeup table rows from makeups + linked students/classes/sessions.
 * Handles: cascading ID extraction, subscription joins, fulfillment readiness, mapping, sort.
 * Uses: semester/search/sort user settings.
 * Used by: MakeupsTable.
 * Refactor: extract join/mapping step into pure adapter for easier test coverage.
 */
import { keyBy, orderBy, uniq } from "lodash"
import { useMemo } from "react"

import { classAtoms, classSessionAtoms, makeupAtoms } from "#atoms"
import { Student } from "#classes/Student"
import { CLASS_GROUP_LABELS } from "#constants/classOptions"
import { USER_SETTINGS } from "#constants/globalConstants"
import {
  getMakeupSortValue,
  MAKEUP_SEARCHABLE_FIELDS,
  MAKEUP_SORTABLE_FIELDS,
} from "#constants/makeupOptions"
import { useUserContext } from "#contexts/UserContextProvider"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  type FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  type MakeupConstructor,
  type MakeupForTable,
} from "#customTypes/composedTypes/makeupTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { SETTINGS_KEYS, type Uuid } from "#customTypes/globalTypes"
import {
  useIsAllFulfilled,
  useIsFulfilled,
  useTimeout,
} from "#hooks/jotai/useCascadingData"
import { useGetSomeStudents } from "#hooks/jotai/useGetStudents"
import {
  useFilteredDocs,
  useSomeDocs,
  useStableIds,
} from "#hooks/jotai/useSubscription"
import useOptimizedSearch from "#hooks/newHooks/useOptimizedSearch"
import { formatISOStringWithTime } from "#utils/dateHelpers"
import { formatSemester } from "#utils/semesterHelpers"
import { getSkillLevelForClassGroup } from "#utils/studentSkillHelpers"
import { camelToWords } from "#utils/textFormatters"

import { useTableSort } from "./useTableSort"

// Returns the student's skill level relevant to the given class group —
// the same level that would appear on their evaluation for that class type,
// resolved for the class's own semester.
const getSkillLevelForClass = (
  student: StudentConstructor | undefined,
  classObj: ClassConstructor | undefined
): string => {
  if (!student || !classObj?.group) return ""
  return getSkillLevelForClassGroup(student, classObj.group, classObj.semester)
}

const useMakeupTableData = () => {
  const { getUserSetting } = useUserContext()
  const filterSemester = getUserSetting<string>(
    USER_SETTINGS.MAKEUPS_TABLE_SEMESTER
  )
  const resultsCount = getUserSetting<number>(
    USER_SETTINGS.MAKEUPS_TABLE_RESULTS_COUNT
  )
  const defaultSearchTerm = getUserSetting<string>(
    USER_SETTINGS.MAKEUPS_TABLE_SEARCH_TERM
  )

  const { sortField, sortOrder, sortRows, onSortChange } =
    useTableSort<MakeupForTable>({
      tableKey: SETTINGS_KEYS.MAKEUPS,
      sortableFields: MAKEUP_SORTABLE_FIELDS,
      getSortValue: getMakeupSortValue,
    })

  const queryConfig = useMemo<FirebaseQueryConfig>(() => {
    if (filterSemester === "All") return {}

    return {
      queries: {
        semester: {
          value: filterSemester,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }
  }, [filterSemester])

  const makeupsFromDb = useFilteredDocs<MakeupConstructor>(
    makeupAtoms,
    DOC_TYPES.MAKEUPS,
    queryConfig
  )

  const makeupsFulfilled = useTimeout(3000, makeupsFromDb.length > 0)

  const studentIds = useMemo(() => {
    if (!makeupsFulfilled) return []
    return uniq(makeupsFromDb.map((makeup) => makeup.studentId))
  }, [makeupsFulfilled, makeupsFromDb])

  const classIds = useMemo(() => {
    if (!makeupsFulfilled) return []

    const ids: Uuid[] = []
    makeupsFromDb.forEach((makeup) => {
      ids.push(makeup.missedClassId)
      if (makeup.isMakeupScheduled && makeup.makeupClassId) {
        ids.push(makeup.makeupClassId)
      }
    })
    return uniq(ids)
  }, [makeupsFulfilled, makeupsFromDb])

  const sessionIds = useMemo(() => {
    if (!makeupsFulfilled) return []

    const ids: Uuid[] = []
    makeupsFromDb.forEach((makeup) => {
      ids.push(makeup.missedSessionId)
      if (makeup.isMakeupScheduled && makeup.makeupSessionId) {
        ids.push(makeup.makeupSessionId)
      }
    })
    return uniq(ids)
  }, [makeupsFulfilled, makeupsFromDb])

  const stableStudentIds = useStableIds(studentIds)
  const stableClassIds = useStableIds(classIds)
  const stableSessionIds = useStableIds(sessionIds)

  const students = useGetSomeStudents(stableStudentIds)

  const classes = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    stableClassIds
  )

  const sessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    stableSessionIds
  )

  const studentsById = useMemo(() => keyBy(students, "id"), [students])
  const classesById = useMemo(() => keyBy(classes, "id"), [classes])
  const sessionsById = useMemo(() => keyBy(sessions, "id"), [sessions])

  // Track fulfillment
  const studentsFulfilled = useIsFulfilled(stableStudentIds, students, 7000)
  const classesFulfilled = useIsFulfilled(stableClassIds, classes, 7000)
  const sessionsFulfilled = useIsFulfilled(stableSessionIds, sessions, 7000)

  const isReady = useIsAllFulfilled([
    makeupsFulfilled,
    studentsFulfilled,
    classesFulfilled,
    sessionsFulfilled,
  ])

  const baseRows: MakeupForTable[] = useMemo(() => {
    if (!makeupsFulfilled || !isReady) return []

    return orderBy(makeupsFromDb, "lastUpdatedAt", "desc").map((makeup) => {
      const student = studentsById[makeup.studentId]
      const missedClass = classesById[makeup.missedClassId]
      const missedSession = sessionsById[makeup.missedSessionId]
      const makeupClassName = makeup.makeupClassId
        ? makeup.makeupClassId
          ? (classesById[makeup.makeupClassId]?.name ?? "⚠️")
          : "⚠️"
        : "Not scheduled"
      const makeupSession = makeup.makeupSessionId
        ? sessionsById[makeup.makeupSessionId]
        : undefined
      const makeupClassSessionDate = makeup.makeupSessionId
        ? formatISOStringWithTime(makeupSession?.eventStart)
        : "Not scheduled"

      return {
        reason: camelToWords(makeup.reason),
        studentName: student?.name,
        studentNameAndDob: student ? new Student(student).getNameAndDob() : "",
        studentDob: student ? new Student(student).getDobString() : "",
        missedClassGroup: missedClass
          ? CLASS_GROUP_LABELS[missedClass.group]
          : "",
        skillLevels: getSkillLevelForClass(student, missedClass),
        missedClassName: missedClass?.name,
        missedClassSessionDate: formatISOStringWithTime(
          missedSession?.eventStart
        ),
        missedClassSessionDateRaw: missedSession?.eventStart ?? "",
        makeupClassName,
        makeupClassSessionDate,
        makeupClassSessionDateRaw: makeupSession?.eventStart ?? "",
        createdAt: formatISOStringWithTime(makeup.createdAt),
        lastUpdatedAt: makeup.lastUpdatedAt,
        id: makeup.id,
        semester: formatSemester(makeup.semester),
      }
    })
  }, [
    makeupsFulfilled,
    makeupsFromDb,
    studentsById,
    classesById,
    sessionsById,
    isReady,
  ])

  const sortedRows = useMemo(() => sortRows(baseRows), [sortRows, baseRows])

  const { filteredItems: filteredMakeups, onSearch } = useOptimizedSearch(
    sortedRows,
    MAKEUP_SEARCHABLE_FIELDS,
    resultsCount,
    defaultSearchTerm
  )

  const makeupsForTable = useMemo(
    () => sortRows(filteredMakeups),
    [sortRows, filteredMakeups]
  )

  return {
    makeupsForTable,
    students,
    classes,
    sessions,
    isReady: isReady && makeupsFromDb?.length === baseRows.length,
    onSearch,
    sortField,
    sortOrder,
    onSortChange,
  }
}

export default useMakeupTableData
